import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Database, 
  ArrowLeft, 
  Globe, 
  Building2, 
  BookOpen, 
  Satellite,
  TrendingUp,
  Leaf,
  ExternalLink,
  CheckCircle2
} from 'lucide-react';

const Datakallor = () => {
  const { language } = useLanguage();

  const content = {
    sv: {
      title: 'Datakällor',
      subtitle: 'Våra beräkningar baseras på officiell statistik och vetenskapliga modeller',
      back: 'Tillbaka till startsidan',
      intro: 'Skogskalkyl använder offentliga datakällor för att säkerställa tillförlitliga beräkningar. Här är de primära källorna vi använder.',
      sections: {
        official: {
          title: 'Officiella svenska datakällor',
          sources: [
            {
              name: 'Skogsstyrelsen',
              description: 'Sveriges skogsmyndighet. Ansvarar för skogsstatistik, virkesprisinformation och skogsbruksregler.',
              data: ['Virkespriser', 'Avverkningsstatistik', 'Skogsvårdsdata'],
              url: 'https://www.skogsstyrelsen.se'
            },
            {
              name: 'SLU Riksskogstaxeringen',
              description: 'Sveriges lantbruksuniversitets riksskogstaxering ger löpande uppgifter om skogstillståndet.',
              data: ['Virkesförråd', 'Tillväxtdata', 'Åldersfördelning'],
              url: 'https://www.slu.se/riksskogstaxeringen'
            },
            {
              name: 'SCB (Statistiska centralbyrån)',
              description: 'Officiell statistik om Sveriges ekonomi och samhälle.',
              data: ['Fastighetspriser', 'Ekonomisk statistik'],
              url: 'https://www.scb.se'
            }
          ]
        },
        scientific: {
          title: 'Vetenskapliga modeller',
          sources: [
            {
              name: 'Brandels volymfunktioner',
              description: 'Standardiserade formler för beräkning av enskilda träds volym, utvecklade av Gunnar Brandel.',
              data: ['Volymberäkning', 'Formtal']
            },
            {
              name: 'Heureka (SLU)',
              description: 'Beslutsstödsystem för skogsbruksplanering med avancerade tillväxtmodeller.',
              data: ['Tillväxtprognoser', 'Skötselsimuleringar'],
              url: 'https://www.slu.se/heureka'
            },
            {
              name: 'IPCC Guidelines',
              description: 'Internationella riktlinjer för beräkning av kollagring i skog.',
              data: ['CO₂-bindning', 'Biomassafaktorer']
            }
          ]
        },
        european: {
          title: 'Europeiska datakällor',
          sources: [
            {
              name: 'Copernicus',
              description: 'EU:s jordobservationsprogram med satellitdata för skogsövervakning.',
              data: ['Satellitbilder', 'Vegetationsindex'],
              upcoming: true
            },
            {
              name: 'EUROSTAT',
              description: 'EU:s statistikbyrå med jämförande europeisk skogsstatistik.',
              data: ['EU skogsstatistik', 'Handelsdata']
            },
            {
              name: 'EU ETS',
              description: 'Europeiska utsläppshandeln för prissättning av koldioxid.',
              data: ['CO₂-priser', 'Kolkrediter']
            }
          ]
        },
        market: {
          title: 'Marknadskällor',
          sources: [
            {
              name: 'VMK (Virkesprismätning)',
              description: 'Regional virkesprisrapportering från skogsägarföreningar.',
              data: ['Aktuella virkespriser', 'Regionala prisuppgifter']
            },
            {
              name: 'LRF Konsult',
              description: 'Fastighetsstatistik och värderingsprinciper för skog.',
              data: ['Fastighetspriser', 'Marknadstrender']
            },
            {
              name: 'Skogforsk',
              description: 'Skogsbrukets forskningsinstitut med data om drivningskostnader.',
              data: ['Drivningskostnader', 'Produktivitetsdata'],
              url: 'https://www.skogforsk.se'
            }
          ]
        }
      },
      futureIntegration: {
        title: 'Framtida dataintegration',
        description: 'Vi arbetar med att integrera fler externa datakällor för ännu mer precisa beräkningar.',
        planned: [
          'Realtidsuppdatering av virkespriser',
          'Satellitbaserad skogsövervakning',
          'Automatisk bonitetsbedömning',
          'Integration med skogsbruksplaner'
        ]
      },
      disclaimer: {
        title: 'Ansvarsfriskrivning',
        text: 'Våra beräkningar ger uppskattningar baserade på statistiska genomsnittsvärden. Faktiska värden kan avvika. Använd resultaten som vägledning, inte som exakta värden. Rådfråga alltid professionell rådgivare för viktiga beslut.'
      }
    },
    de: {
      title: 'Datenquellen',
      subtitle: 'Unsere Berechnungen basieren auf offiziellen Statistiken und wissenschaftlichen Modellen',
      back: 'Zurück zur Startseite',
      intro: 'Skogskalkyl verwendet öffentliche Datenquellen, um zuverlässige Berechnungen zu gewährleisten.',
      futureIntegration: {
        title: 'Zukünftige Datenintegration',
        description: 'Wir arbeiten an der Integration weiterer externer Datenquellen.'
      },
      disclaimer: {
        title: 'Haftungsausschluss',
        text: 'Unsere Berechnungen liefern Schätzungen basierend auf statistischen Durchschnittswerten.'
      }
    },
    en: {
      title: 'Data Sources',
      subtitle: 'Our calculations are based on official statistics and scientific models',
      back: 'Back to homepage',
      intro: 'Skogskalkyl uses public data sources to ensure reliable calculations. Here are the primary sources we use.',
      futureIntegration: {
        title: 'Future Data Integration',
        description: 'We are working on integrating more external data sources for even more precise calculations.',
        planned: [
          'Real-time timber price updates',
          'Satellite-based forest monitoring',
          'Automatic site quality assessment',
          'Integration with forest management plans'
        ]
      },
      disclaimer: {
        title: 'Disclaimer',
        text: 'Our calculations provide estimates based on statistical averages. Actual values may vary. Use the results as guidance, not as exact values.'
      }
    },
    se: {
      title: 'Diehtogáldut',
      subtitle: 'Min rehkenastimat vuođđuduvvojit virggálaš statistihkkii',
      back: 'Ruoktot',
      intro: 'Skogskalkyl geavaha almmolaš diehtogálduid.'
    }
  };

  const c = content[language] || content.sv;
  const sections = c.sections || content.sv.sections;
  const futureIntegration = c.futureIntegration || content.sv.futureIntegration;
  const disclaimer = c.disclaimer || content.sv.disclaimer;

  const sectionIcons = {
    official: Building2,
    scientific: BookOpen,
    european: Globe,
    market: TrendingUp
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="datakallor-page">
      <Link 
        to="/" 
        className="inline-flex items-center text-sm text-forest-600 hover:text-forest-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {c.back}
      </Link>

      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-forest-800 flex items-center justify-center gap-3">
          <Database className="h-8 w-8" />
          {c.title}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">{c.subtitle}</p>
      </div>

      <p className="text-lg text-center text-muted-foreground">{c.intro}</p>

      {/* Data Source Sections */}
      <div className="space-y-6">
        {Object.entries(sections).map(([key, section]) => {
          const Icon = sectionIcons[key] || Database;
          return (
            <Card key={key}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-forest-700">
                  <Icon className="h-5 w-5" />
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {section.sources.map((source, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-forest-800 flex items-center gap-2">
                            {source.name}
                            {source.upcoming && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Planerad
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{source.description}</p>
                        </div>
                        {source.url && (
                          <a 
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-forest-600 hover:text-forest-700"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {source.data.map((item, i) => (
                          <span 
                            key={i}
                            className="text-xs bg-forest-100 text-forest-700 px-2 py-1 rounded"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Future Integration */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Satellite className="h-5 w-5" />
            {futureIntegration.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-800 mb-4">{futureIntegration.description}</p>
          <ul className="space-y-2">
            {futureIntegration.planned?.map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-blue-700">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                {item}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <Leaf className="h-5 w-5" />
            {disclaimer.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-amber-800">{disclaimer.text}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Datakallor;
